import React, { useRef } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { getEntryHighlight, IGeographyConstraint, IGeographyEntry, splitByRanges } from './geography-name-filter';
import styles from './geography-codes-cell.module.scss';

interface IGeographyCodeProperties {
  entry: IGeographyEntry;
  constraints: IGeographyConstraint[];
  highlightCode: boolean;
}

function GeographyCode({ entry, constraints, highlightCode }: IGeographyCodeProperties) {
  const reference = useRef<HTMLSpanElement>(null);
  const { nameMatches, nameRanges } = getEntryHighlight(entry, constraints);
  const className = highlightCode && nameMatches ? `${styles.code} ${styles.codeMatch}` : styles.code;

  if (!entry.name) {
    return <span className={className}>{entry.code}</span>;
  }

  return (
    <>
      <span ref={reference} className={className} tabIndex={0}>
        {entry.code}
      </span>
      <Tooltip target={reference} event="both" position="top">
        {splitByRanges(entry.name, nameRanges).map((part, index) =>
          part.marked ? (
            <mark key={index} className={styles.nameMatch}>
              {part.text}
            </mark>
          ) : (
            <React.Fragment key={index}>{part.text}</React.Fragment>
          )
        )}
      </Tooltip>
    </>
  );
}

interface IGeographyCodesCellProperties {
  entries: IGeographyEntry[];
  constraints: IGeographyConstraint[];
}

export function GeographyCodesCell({ entries, constraints }: IGeographyCodesCellProperties) {
  // A code is only highlighted to show which entry's name matched, which matters only in a list.
  const highlightCode = entries.length > 1;
  return (
    <>
      {entries.map((entry, index) => (
        <React.Fragment key={entry.code}>
          {index > 0 && ', '}
          <GeographyCode entry={entry} constraints={constraints} highlightCode={highlightCode} />
        </React.Fragment>
      ))}
    </>
  );
}
